// eslint-disable-next-line no-restricted-imports
import {
  Configuration,
  GetInstrumentByCategoriesResponse,
  DefaultApi as InstrtumentApi,
  DefaultApiBatchInstrumentQueryRequest as InstrumentApiBatchInstrumentQueryRequest,
  DefaultApiBatchListedInstrumentQueryRequest as InstrumentApiBatchListedInstrumentQueryRequest,
  DefaultApiGetInstrumentTickSizeRequest as InstrumentApiDefaultApiGetInstrumentTickSizeRequest,
  DefaultApiGetInstrumentByCategoriesRequest as InstrumentApiGetInstrumentByCategoriesRequest,
  GetInstrumentMarketSummaryResponse as InstrumentApiGetInstrumentMarketSummaryResponse,
  GetInstrumentQueryInitDataResponse as InstrumentApiGetInstrumentQueryInitDataResponse,
  GetTopTradedListedInstruments200ResponseInner as InstrumentApiGetTopTradedListedInstruments200ResponseInner,
  DefaultApiGetTopTradedListedInstrumentsRequest as InstrumentApiGetTopTradedListedInstrumentsRequest,
  InstrumentSector as InstrumentApiInstrumentSector,
  MarketDataProviderDetail as InstrumentApiMarketDataProviderDetail,
  MarketSubCategory as InstrumentApiMarketSubCategory,
  DefaultApiSearchListedInstrumentRequest as InstrumentApiSearchListedInstrumentRequest,
  SectorImageEnum as InstrumentApiSectorImageEnum,
} from '@eih-netbroker/api-instrument';
import { AxiosInstance, AxiosResponse } from 'axios';
import { IConfigAPI } from '../config';
import { ResourceHeadersWithToken } from '../services/useResource';
import { ListedInstrument } from '../types';
import { BaseResource } from './base.resource';

export type BatchInstrumentQueryRequest = Omit<
  InstrumentApiBatchInstrumentQueryRequest,
  keyof ResourceHeadersWithToken
>;

export type BatchInstrumentQueryRequestQueueElement = {
  request: BatchListedInstrumentQueryRequest;
  headers: ResourceHeadersWithToken;
  resolver: (response: AxiosResponse<ListedInstrument[]>) => void;
  reject: (error: unknown) => void;
};

export type BatchListedInstrumentQueryRequest = Omit<
  InstrumentApiBatchListedInstrumentQueryRequest,
  keyof ResourceHeadersWithToken
>;

export type GetInstrumentTickSizeRequest = Omit<
  InstrumentApiDefaultApiGetInstrumentTickSizeRequest,
  keyof ResourceHeadersWithToken
>;

export type GetInstrumentByCategoriesRequest = Omit<
  InstrumentApiGetInstrumentByCategoriesRequest,
  keyof ResourceHeadersWithToken
>;

export type SearchListedInstrumentRequest = Omit<
  InstrumentApiSearchListedInstrumentRequest,
  keyof ResourceHeadersWithToken
>;

export type GetTopTradedListedInstrumentsRequest = Omit<
  InstrumentApiGetTopTradedListedInstrumentsRequest,
  keyof ResourceHeadersWithToken
>;

export type TopTradedListedInstrumentsResponseItem = InstrumentApiGetTopTradedListedInstruments200ResponseInner;

export type GetInstrumentMarketSummaryResponse = InstrumentApiGetInstrumentMarketSummaryResponse;
export type GetInstrumentQueryInitDataResponse = InstrumentApiGetInstrumentQueryInitDataResponse;
export type InstrumentSector = InstrumentApiInstrumentSector;
export type SectorImageEnum = InstrumentApiSectorImageEnum;
export type MarketDataProviderDetail = InstrumentApiMarketDataProviderDetail;
export type MarketSubCategory = InstrumentApiMarketSubCategory;

export class InstrumentResource extends BaseResource {
  // Name has to be overridden, since the build job renames it to 'r' and
  // every resource class will have the same name
  // @ts-ignore
  static readonly name = 'InstrumentResource';
  private instrumentApi: InstrtumentApi;
  private batchListedInstrumentQueue: Array<BatchInstrumentQueryRequestQueueElement> = [];
  private timer: NodeJS.Timeout | null = null;

  constructor(axiosInstance: AxiosInstance, basePath: string, configApi: IConfigAPI) {
    super(axiosInstance, `${basePath}${configApi.usUrl.instrument}`, configApi);
    this.instrumentApi = new InstrtumentApi(
      new Configuration(),
      `${basePath}${configApi.usUrl.instrument}`,
      axiosInstance,
    );
  }

  async searchListedInstrument(request: SearchListedInstrumentRequest, headers: ResourceHeadersWithToken) {
    return this.instrumentApi.searchListedInstrument({ ...request, ...headers });
  }

  async batchInstrumentQuery(request: BatchInstrumentQueryRequest, headers: ResourceHeadersWithToken) {
    return this.instrumentApi.batchInstrumentQuery({ ...request, ...headers });
  }

  async batchListedInstrumentQuery(request: BatchListedInstrumentQueryRequest, headers: ResourceHeadersWithToken) {
    return new Promise<AxiosResponse<ListedInstrument[]>>((resolver, reject) => {
      this.batchListedInstrumentQueue.push({ request, headers, resolver, reject });

      if (!this.timer) {
        this.timer = setTimeout(() => this.flush(), this.configApi.batchedRequestsTimeout.instrument);
      }
    });
  }

  async getInstrumentTickSize(request: GetInstrumentTickSizeRequest, headers: ResourceHeadersWithToken) {
    return this.instrumentApi.getInstrumentTickSize({ ...request, ...headers });
  }

  async getInstrumentByCategories(
    request: GetInstrumentByCategoriesRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<GetInstrumentByCategoriesResponse>> {
    return this.instrumentApi.getInstrumentByCategories({ ...request, ...headers });
  }

  async getTopTradedListedInstruments(
    request: GetTopTradedListedInstrumentsRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<TopTradedListedInstrumentsResponseItem[]>> {
    return this.instrumentApi.getTopTradedListedInstruments({ ...request, ...headers });
  }

  async getInstrumentQueryInitData(
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<GetInstrumentQueryInitDataResponse>> {
    return this.instrumentApi.getInstrumentQueryInitData({ ...headers });
  }

  async getInstrumentMarketSummary(
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<GetInstrumentMarketSummaryResponse>> {
    return this.instrumentApi.getInstrumentMarketSummary({ ...headers });
  }

  getVersion() {
    return this.instrumentApi.getVersion();
  }

  async flush() {
    if (this.batchListedInstrumentQueue.length === 0) {
      return;
    }

    const batchedRequests = this.batchListedInstrumentQueue.map(({ request, headers, resolver, reject }) => ({
      request,
      headers,
      reject,
      resolver,
    }));

    this.batchListedInstrumentQueue = [];
    this.timer = null;

    try {
      const responses = await this.instrumentApi.batchListedInstrumentQuery({
        rics: batchedRequests.reduce((acc, { request }) => [...acc, ...request.rics!], [] as string[]),
        ...batchedRequests[0].headers,
      });

      batchedRequests.forEach(({ request, resolver }) =>
        resolver({
          ...responses,
          data: responses.data.filter((instrument: ListedInstrument) => request.rics?.includes(instrument.ric)),
        }),
      );
    } catch (error) {
      batchedRequests.forEach(({ reject }) => reject(error));
    }
  }
}
